/** Start a circle: pick a group the room made (or the people a search found), name it, and go live with them at once. */
import { type ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { CircleCreateRequest, HostRoomRow, TalkAnswerGroup, TalkFace } from "../../shared/talk";
import type { RoomGroupKey, RoomGroupPeopleResponse, RoomPerson } from "../../shared/types";
import { Button } from "../components/ui/Button";
import { FaceStack } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { Toggle } from "../components/ui/Toggle";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import type { CircleSeed } from "./handoff";
import { AfterSecondLive, facesOf, pickedSet, SessionSwitch } from "./parts";
import { PeopleRow, type Pickable, PickList } from "./Pick";
import { circlePath } from "./places";
import { useRemote } from "./useTalk";
import { toldWords } from "./when";

export interface CirclePeople {
  registrantIds: string[];
  faces: TalkFace[];
  /** The rooms their messages came from, when more than one: the sheet asks which. */
  roomIds?: string[];
}

/** The people who picked one choice of a poll in a recording: a circle made from them. */
export interface CircleAnswer {
  answer: TalkAnswerGroup;
  count: number;
  faces: TalkFace[];
  /** Said under the people: what they answered. */
  words: string;
}

const pickableOf = (p: RoomPerson): Pickable => ({ registrantId: p.registrantId, name: p.name, photoUrl: p.avatar_url, fact: p.note || p.stay });

/** Every person of a group, page after page: the list Choose ticks from. */
async function groupPeople(readPage: (cursor?: string) => Promise<RoomGroupPeopleResponse>): Promise<Pickable[]> {
  const out: Pickable[] = [];
  let cursor: string | undefined;
  do {
    const page = await readPage(cursor);
    out.push(...page.people.map(pickableOf));
    cursor = page.next ?? undefined;
  } while (cursor);
  return out;
}

export function NewCircleSheet({ rooms, webinarId, seed, people, answer, phone, onClose, onMade }: {
  rooms: HostRoomRow[];
  webinarId: string | null;
  seed?: CircleSeed;
  /** Set when the circle is made from the people a search found, not from a group. */
  people?: CirclePeople;
  /** Set when the circle is made from the people who picked one choice of a poll. */
  answer?: CircleAnswer;
  phone: boolean;
  onClose: () => void;
  onMade: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [wid, setWid] = useState<string | null>(webinarId ?? (rooms.length === 1 ? (rooms[0]?.webinarId ?? null) : null));
  const room = rooms.find((r) => r.webinarId === wid) ?? null;
  const { data } = useRemote(wid && !people && !answer ? `talk:sheet:${wid}` : null, () => api.talk.everyone(wid ?? ""));
  const sets = data?.groups ?? [];
  const [all, setAll] = useState(seed?.scope === "all" || seed?.scope === "series");
  const [pick, setPick] = useState<string | null>(seed?.key ?? null);
  const [name, setName] = useState<string | null>(null);
  const [now, setNow] = useState(true);
  const [talk, setTalk] = useState(true);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [only, setOnly] = useState<string[] | null>(null);
  const [list, setList] = useState<{ key: string; people: Pickable[] } | "failed" | null>(null);
  const [ticks, setTicks] = useState<Set<string>>(new Set());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = pickedSet(sets, all);
  const groups = (set?.groups ?? []).filter((g) => g.count > 0);
  const group = groups.find((g) => g.key === pick) ?? groups[0] ?? null;
  const groupKey = answer ? `answer:${answer.answer.interactionId}:${answer.answer.option}` : `${set?.scope}:${set?.session?.id ?? ""}:${group?.key}`;
  const everyone = people ? people.registrantIds.length : answer ? answer.count : (group?.count ?? 0);
  const count = only ? only.length : everyone;
  const shownName = name ?? (people ? "" : answer ? answer.answer.option : group ? `${group.name} circle` : "");
  const needsRoom = !wid;
  const choices = people?.roomIds?.length ? rooms.filter((r) => people.roomIds?.includes(r.webinarId)) : rooms;

  // Why reset: another group is another list of people, so the ticks start again from everyone.
  useEffect(() => {
    setOnly(null);
  }, [groupKey]);

  const choose = () => {
    setPicking(true);
    if ((!group && !answer) || !wid) return;
    const listed = list !== null && list !== "failed" && list.key === groupKey ? list.people : null;
    if (listed) {
      setTicks(new Set(only ?? listed.map((x) => x.registrantId)));
      return;
    }
    setList(null);
    const session = set?.session?.id ?? (set?.scope === "session" ? undefined : set?.scope);
    groupPeople((cursor) => (answer ? api.talk.answerPeople(answer.answer, cursor) : api.webinars.audienceGroup(wid, group?.key as RoomGroupKey, session, cursor))).then(
      (all) => {
        setList({ key: groupKey, people: all });
        setTicks(new Set(only ?? all.map((x) => x.registrantId)));
      },
      () => setList("failed"),
    );
  };
  const listed = list !== null && list !== "failed" && list.key === groupKey ? list.people : null;
  const done = () => {
    if (listed && ticks.size > 0) setOnly(ticks.size === listed.length ? null : [...ticks]);
    setPicking(false);
  };
  const toggle = (id: string) =>
    setTicks((t) => {
      const next = new Set(t);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const start = async () => {
    if (!wid || !shownName.trim() || busy || (!people && !answer && !group)) return;
    setBusy(true);
    const madeFrom: CircleCreateRequest["madeFrom"] = people
      ? { registrantIds: people.registrantIds }
      : answer
        ? { answer: answer.answer, ...(only ? { only } : {}) }
        : { scope: set?.scope ?? "session", key: group?.key ?? "came", ...(set?.session ? { session: set.session.id } : {}), ...(only ? { only } : {}) };
    try {
      const made = await api.talk.createCircle({ webinarId: wid, name: shownName.trim(), madeFrom, membersCanTalk: talk, goLive: now, requestId: crypto.randomUUID() });
      onMade();
      onClose();
      navigate(made.call ? made.call.studioPath : circlePath(made.circleId));
    } catch {
      toast({ text: "We couldn't start the circle. Try again.", icon: "info" });
      setBusy(false);
    }
  };

  const told = toldWords(count, phone);
  const body: ReactNode = (
    <>
      {needsRoom ? (
        <>
          <p className={phone ? "hp-sheet-l" : "sheet-l"}>Which room is it in?</p>
          <div className="picks">
            {choices.map((r) => (
              <button key={r.webinarId} type="button" className="pick" onClick={() => setWid(r.webinarId)}>
                <span className="pick-n">{r.title}</span>
                <b>{r.people.toLocaleString("en-US")}</b>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          {people ? (
            <div className="picks">
              <span className="pick on">
                <FaceStack people={facesOf(people.faces.slice(0, 3))} size={24} />
                <span className="pick-n">The people who asked</span>
                <b>{people.registrantIds.length}</b>
              </span>
            </div>
          ) : answer ? (
            <PeopleRow faces={answer.faces} count={count} total={answer.count} note={answer.words} onChoose={choose} />
          ) : (
            <>
              {sets.length > 1 ? <SessionSwitch sets={sets} all={all} onAll={setAll} /> : null}
              {groups.length === 0 && set?.scope === "series" ? <AfterSecondLive /> : null}
              <div className="picks">
                {groups.map((g) => (
                  <button key={g.key} type="button" className={group?.key === g.key ? "pick on" : "pick"} onClick={() => setPick(g.key)}>
                    <FaceStack people={facesOf(g.faces.slice(0, 3))} size={24} />
                    <span className="pick-n">{g.name}</span>
                    <b>{g.count}</b>
                  </button>
                ))}
              </div>
              {group ? <PeopleRow faces={group.faces} count={count} total={group.count} note={group.name} onChoose={choose} /> : null}
            </>
          )}
          <label className="field">
            <span>Name</span>
            <input value={shownName} onChange={(e) => setName(e.target.value)} />
          </label>
          <div className={now ? "golive on" : "golive"}>
            <span className="golive-ic">
              <Icon name="live" />
            </span>
            <span>
              <b>Go live with them now</b>
              <small>{told}</small>
            </span>
            <Toggle on={now} onChange={setNow} label="Go live with them now" />
          </div>
          <div className="set">
            <span>
              <b>They can talk to each other</b>
              <small>Off: only you post, they reply to you</small>
            </span>
            <Toggle on={talk} onChange={setTalk} label="They can talk to each other" />
          </div>
        </>
      )}
    </>
  );
  const startLabel = only ? `Start a circle of ${count.toLocaleString("en-US")}` : now ? "Start the circle and go live" : "Start the circle";
  const disabled = busy || needsRoom || !shownName.trim() || (!people && !answer && !group);

  const picker = (
    <>
      {listed ? (
        <PickList people={listed} chosen={ticks} onToggle={toggle} onAll={(on) => setTicks(new Set(on ? listed.map((x) => x.registrantId) : []))} />
      ) : list === "failed" ? (
        <p className="empty-note">We couldn't load the people. Go back and try again.</p>
      ) : (
        <p className="empty-note">Loading the people</p>
      )}
    </>
  );
  const chosen = <span className="pk-n">{ticks.size.toLocaleString("en-US")} chosen</span>;
  const cantDone = !listed || ticks.size === 0;

  if (picking && phone) {
    return (
      <div className="hp-veil" onClick={onClose}>
        <div className="hp-sheet full" role="dialog" aria-label="Who is in the circle" onClick={(e) => e.stopPropagation()}>
          <span className="hp-grab" aria-hidden="true" />
          <header className="hp-sheet-h back">
            <button type="button" className="hp-ib" aria-label="Back" onClick={done}>
              <Icon name="left" />
            </button>
            <h2>Who is in the circle</h2>
          </header>
          <div className="hp-sheet-b">{picker}</div>
          <div className="hp-sheet-f">
            <div className="pk-foot">
              {chosen}
              <button type="button" className="hp-primary" disabled={cantDone} onClick={done}>
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (picking) {
    return (
      <div className="veil" onClick={onClose}>
        <div className="card sheet pk-sheet" role="dialog" aria-label="Who is in the circle" onClick={(e) => e.stopPropagation()}>
          <header className="pk-h">
            <button type="button" className="hp-ib" aria-label="Back" onClick={done}>
              <Icon name="left" />
            </button>
            <h2>Who is in the circle</h2>
          </header>
          {picker}
          <div className="sheet-acts pk-acts">
            {chosen}
            <Button variant="p" disabled={cantDone} onClick={done}>
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (phone) {
    return (
      <div className="hp-veil" onClick={onClose}>
        <div className="hp-sheet" role="dialog" aria-label="Start a circle" onClick={(e) => e.stopPropagation()}>
          <span className="hp-grab" aria-hidden="true" />
          <header className="hp-sheet-h">
            <h2>Start a circle</h2>
            <button type="button" className="hp-ib" aria-label="Close" onClick={onClose}>
              <Icon name="x" />
            </button>
          </header>
          <div className="hp-sheet-b">
            {!needsRoom ? <p className="hp-sheet-l">Who is in it?</p> : null}
            {body}
          </div>
          <div className="hp-sheet-f">
            <button type="button" className="hp-primary" disabled={disabled} onClick={() => void start()}>
              <Icon name={now ? "live" : "target"} />
              {startLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="veil" onClick={onClose}>
      <div className="card sheet" role="dialog" aria-label="Start a circle" onClick={(e) => e.stopPropagation()}>
        <h2>Start a circle</h2>
        {!needsRoom ? <p className="sheet-l">A small private group inside {room?.title}. Who is in it?</p> : null}
        {body}
        <div className="sheet-acts">
          <Button onClick={onClose}>Not now</Button>
          <Button variant="p" icon={now ? "live" : "target"} disabled={disabled} onClick={() => void start()}>
            {startLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
